import { getUsageStatus } from "./budget";
import { DEFAULT_SCORE_TEMPLATES } from "./customerValueCalculation";
import {
  getPackageEndDate,
  getPackageLimit,
  isEnterpriseRequired
} from "./packageLimits";
import {
  customerValueCriteriaSeed,
  customerValueModelsSeed,
  customerValueScoreTemplatesSeed,
  adminAuditLogs,
  departmentResponses,
  departments,
  organizations,
  users,
  workspaceAiSummaries,
  workspaceUsage,
  workspaces
} from "./mockData";
import type {
  CustomerValueCriterion,
  CustomerValueModel,
  CustomerValueScoreTemplate,
  Department,
  DepartmentResponse,
  DepartmentStatus,
  Organization,
  PackageType,
  User,
  Workspace,
  AdminAuditLog,
  WorkspaceUsage,
  WorkshopAnswers,
  WorkspaceAiSummary
} from "../types";

const STORE_KEY = "hauffe-os-workspace-store-v2";

export interface WorkspaceStoreState {
  organizations: Organization[];
  users: User[];
  workspaces: Workspace[];
  departments: Department[];
  departmentResponses: DepartmentResponse[];
  workspaceUsage: WorkspaceUsage[];
  workspaceAiSummaries: WorkspaceAiSummary[];
  customerValueModels: CustomerValueModel[];
  customerValueCriteria: CustomerValueCriterion[];
  customerValueScoreTemplates: CustomerValueScoreTemplate[];
  adminAuditLogs: AdminAuditLog[];
}

export interface WorkspaceCreateInput {
  organizationName: string;
  workspaceName: string;
  packageType: PackageType;
  startDate: string;
  departmentCount: number;
  ownerEmail: string;
}

export function createSeedState(): WorkspaceStoreState {
  return {
    organizations: structuredClone(organizations),
    users: structuredClone(users),
    workspaces: structuredClone(workspaces),
    departments: structuredClone(departments),
    departmentResponses: structuredClone(departmentResponses),
    workspaceUsage: [structuredClone(workspaceUsage)],
    workspaceAiSummaries: structuredClone(workspaceAiSummaries),
    customerValueModels: structuredClone(customerValueModelsSeed),
    customerValueCriteria: structuredClone(customerValueCriteriaSeed),
    customerValueScoreTemplates: structuredClone(customerValueScoreTemplatesSeed),
    adminAuditLogs: structuredClone(adminAuditLogs)
  };
}

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function getWorkspaceState(): WorkspaceStoreState {
  if (!canUseStorage()) return createSeedState();

  const raw = window.localStorage.getItem(STORE_KEY);
  if (!raw) {
    const seed = createSeedState();
    saveWorkspaceState(seed);
    return seed;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<WorkspaceStoreState>;
    return {
      ...createSeedState(),
      ...parsed
    };
  } catch {
    const seed = createSeedState();
    saveWorkspaceState(seed);
    return seed;
  }
}

export function saveWorkspaceState(state: WorkspaceStoreState): void {
  if (!canUseStorage()) return;
  window.localStorage.setItem(STORE_KEY, JSON.stringify(state));
}

function slugifyId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

function nowIso(): string {
  return new Date().toISOString();
}

export function createOrganizationIfNeeded(state: WorkspaceStoreState, name: string): Organization {
  const normalizedName = name.trim();
  const existing = state.organizations.find(
    (organization) => organization.name.toLowerCase() === normalizedName.toLowerCase()
  );
  if (existing) return existing;

  const organization: Organization = {
    id: `org-${slugifyId(normalizedName || "organization")}-${Date.now()}`,
    name: normalizedName || "Neue Organisation",
    industry: "Noch nicht angegeben",
    companySize: "Noch nicht angegeben"
  };
  state.organizations.push(organization);
  return organization;
}

export function createOrGetCompanyOwner(state: WorkspaceStoreState, email: string, organizationId: string): User {
  const normalizedEmail = email.trim().toLowerCase();
  const existing = state.users.find((user) => user.email.toLowerCase() === normalizedEmail);
  if (existing) return existing;

  const user: User = {
    id: `user-owner-${slugifyId(normalizedEmail || "owner")}-${Date.now()}`,
    email: normalizedEmail,
    firstName: "Company",
    lastName: "Owner",
    role: "company_owner",
    organizationId,
    isActive: true
  };
  state.users.push(user);
  return user;
}

export function createOrGetDepartmentContributor(state: WorkspaceStoreState, email: string, organizationId: string, name: string): User {
  const normalizedEmail = email.trim().toLowerCase();
  const existing = state.users.find((user) => user.email.toLowerCase() === normalizedEmail);
  if (existing) return existing;

  const user: User = {
    id: `user-contributor-${slugifyId(normalizedEmail || name)}-${Date.now()}`,
    email: normalizedEmail,
    name,
    firstName: name,
    lastName: "",
    role: "department_contributor",
    organizationId,
    isActive: true
  };
  state.users.push(user);
  return user;
}

export function createWorkspaceUsage(workspaceId: string, packageType: PackageType): WorkspaceUsage {
  const limit = getPackageLimit(packageType);
  return {
    workspaceId,
    aiInputTokensUsed: 0,
    aiOutputTokensUsed: 0,
    aiCallsUsed: 0,
    aiCostEstimateEur: 0,
    cloudflareCostEstimateEur: 0,
    techCostEstimateEur: 0,
    techBudgetLimitEur: limit.techBudgetLimitEur,
    usageStatus: getUsageStatus(0, limit.techBudgetLimitEur)
  };
}

export function createWorkspace(state: WorkspaceStoreState, input: WorkspaceCreateInput): Workspace {
  if (isEnterpriseRequired(input.departmentCount)) {
    throw new Error("Für mehr als 10 Abteilungen wird Enterprise Access individuell eingerichtet.");
  }

  const organization = createOrganizationIfNeeded(state, input.organizationName);
  const owner = createOrGetCompanyOwner(state, input.ownerEmail, organization.id);
  const workspaceId = `workspace-${slugifyId(input.workspaceName || organization.name)}-${Date.now()}`;
  const workspace: Workspace = {
    id: workspaceId,
    organizationId: organization.id,
    name: input.workspaceName.trim() || "Customer Value Workspace",
    packageType: input.packageType,
    startDate: input.startDate,
    endDate: getPackageEndDate(input.startDate, input.packageType),
    status: "active",
    goal: `Customer-Value-Logik mit ${input.departmentCount} Abteilungen aufbauen.`,
    focusType: "both",
    maxDepartments: input.departmentCount,
    createdByUserId: owner.id
  };

  state.workspaces.push(workspace);
  state.workspaceUsage.push(createWorkspaceUsage(workspaceId, input.packageType));
  return workspace;
}

export function getWorkspaceById(state: WorkspaceStoreState, id: string): Workspace | undefined {
  return state.workspaces.find((workspace) => workspace.id === id);
}

export function listWorkspaces(state: WorkspaceStoreState): Workspace[] {
  return state.workspaces;
}

export function getDepartmentResponse(
  state: WorkspaceStoreState,
  departmentId: string
): DepartmentResponse | undefined {
  return state.departmentResponses.find((response) => response.departmentId === departmentId);
}

export function updateDepartmentStatus(
  state: WorkspaceStoreState,
  departmentId: string,
  status: DepartmentStatus
): Department | undefined {
  const department = state.departments.find((item) => item.id === departmentId);
  if (!department) return undefined;
  department.status = status;
  return department;
}

function upsertDepartmentResponse(
  state: WorkspaceStoreState,
  departmentId: string,
  answers: WorkshopAnswers,
  responseStatus: "draft" | "submitted"
): DepartmentResponse {
  const department = state.departments.find((item) => item.id === departmentId);
  if (!department) {
    throw new Error("Abteilung nicht gefunden.");
  }

  const existingIndex = state.departmentResponses.findIndex((response) => response.departmentId === departmentId);
  const existing = existingIndex >= 0 ? state.departmentResponses[existingIndex] : undefined;
  const now = nowIso();
  const response: DepartmentResponse = {
    id: existing?.id ?? `response_${departmentId}`,
    workspaceId: department.workspaceId,
    departmentId,
    userId: existing?.userId,
    responseStatus,
    answers,
    submittedAt: responseStatus === "submitted" ? now : existing?.submittedAt,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now
  };

  if (existingIndex >= 0) {
    state.departmentResponses[existingIndex] = response;
  } else {
    state.departmentResponses.push(response);
  }

  updateDepartmentStatus(state, departmentId, responseStatus === "submitted" ? "submitted" : "started");
  return response;
}

export function startDepartmentById(state: WorkspaceStoreState, departmentId: string): Department | undefined {
  const department = state.departments.find((item) => item.id === departmentId);
  if (!department) return undefined;
  if (department.status === "invited") department.status = "started";
  return department;
}

export function saveDepartmentDraft(
  state: WorkspaceStoreState,
  departmentId: string,
  answers: WorkshopAnswers
): DepartmentResponse {
  return upsertDepartmentResponse(state, departmentId, answers, "draft");
}

export function submitDepartmentResponse(
  state: WorkspaceStoreState,
  departmentId: string,
  answers: WorkshopAnswers
): DepartmentResponse {
  return upsertDepartmentResponse(state, departmentId, answers, "submitted");
}

export function createCustomerValueModelFromAiSummary(
  state: WorkspaceStoreState,
  workspaceId: string,
  summaryId: string
): CustomerValueModel {
  const existing = state.customerValueModels.find((model) => model.workspaceId === workspaceId);
  if (existing) return existing;

  const now = nowIso();
  const model: CustomerValueModel = {
    id: `cv-model-${workspaceId}`,
    workspaceId,
    name: "Customer Value Calculation",
    status: "draft",
    sourceAiSummaryId: summaryId,
    createdAt: now,
    updatedAt: now
  };
  state.customerValueModels.push(model);

  const summary = state.workspaceAiSummaries.find((item) => item.id === summaryId);
  const suggestions = summary?.result.customer_value_criteria_suggestions ?? [];
  suggestions.forEach((suggestion, index) => {
    state.customerValueCriteria.push({
      id: `criterion-${model.id}-${index + 1}`,
      modelId: model.id,
      workspaceId,
      name: suggestion.name,
      description: suggestion.description,
      criterionType: suggestion.type,
      weight: suggestion.type === "negative" ? 15 : 20,
      sortOrder: index + 1,
      isActive: true,
      source: "ai_suggested",
      createdAt: now,
      updatedAt: now
    });
  });

  DEFAULT_SCORE_TEMPLATES.forEach((template, index) => {
    state.customerValueScoreTemplates.push({
      id: `score-template-${model.id}-${index + 1}`,
      modelId: model.id,
      workspaceId,
      ...template,
      createdAt: now,
      updatedAt: now
    });
  });

  return model;
}
